// ============================================================================
// RECEIPT SERVICE — entity-level receipt capture + register (ADR-0024 Leg 1).
// Post-on-create: POST /receipts posts the Bank Receipt voucher. Scoped via
// X-Entity-ID / X-Business-Unit-ID / X-Financial-Year-ID headers (interceptor).
// ============================================================================

import { get, post } from '../client/axiosClient';
import { receiptEndpoints } from '../endpoints/banking.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  ID,
  ReceiptDto,
  CreateReceiptRequest,
  AssignReceiptRequest,
  ReceiptQueryParams,
  AssignmentTargetDto,
} from '../types';

class ReceiptService {
  /** Paged receipt register. */
  async getAll(params?: ReceiptQueryParams): Promise<PaginatedResponse<ReceiptDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(receiptEndpoints.list(), params)
      : receiptEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<ReceiptDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /** Full receipt including its assignments (the list omits them). */
  async getById(id: ID): Promise<ReceiptDto> {
    const response = await get<ApiResponse<ReceiptDto>>(receiptEndpoints.getById(id));
    return response.data.data;
  }

  /** Capture a receipt (posts the Bank Receipt voucher on save). */
  async create(data: CreateReceiptRequest): Promise<ReceiptDto> {
    const response = await post<ApiResponse<ReceiptDto>>(receiptEndpoints.create(), data);
    return response.data.data;
  }

  /** Assign the unassigned remainder — or redistribute a RETURNED portion — to BUs. */
  async assign(id: ID, data: AssignReceiptRequest): Promise<ReceiptDto> {
    const response = await post<ApiResponse<ReceiptDto>>(receiptEndpoints.assign(id), data);
    return response.data.data;
  }

  /** All active entities + BUs of the tenant — the assignment target picker (ADR-0026). */
  async getAssignmentTargets(): Promise<AssignmentTargetDto[]> {
    const response = await get<ApiResponse<AssignmentTargetDto[]>>(receiptEndpoints.assignmentTargets());
    return response.data.data ?? [];
  }
}

export const receiptService = new ReceiptService();
export default receiptService;
